import React, { useState } from 'react';
import {
    ValidationForm,
    Grid,
    Cell,
    FieldHeight,
    Box,
    P,
} from '@saladbob/sassafras';


import { AlertTriangle } from 'react-feather';

import { Radio, DOB, Experience, Tshirt } from './Fields';
import { Text } from './Fields';

import type { User } from '../../types';

type Props = {
    user: User,
    showType?: boolean,
    showGender?: boolean,
    showName?: boolean,
    showWarning?: boolean,
    onChange? (user: User): void,
    onSubmit: Function,
    children?: React.ReactNode,
    formRef? : React.RefObject<React.Component>
};

const columns = {
    phone: 'span 4',
    laptop: 'span 2',
}

const UserProfile: React.FC<Props> = ({
    user,
    onSubmit,
    showType = true,
    showGender = false,
    showName = false,
    showWarning = false,
    onChange,
    children,
    formRef: ref,
}) => {
    const [role, setRole] = useState(showType ? user.type : 'player');
    const [newUser, setUser] = useState(user);

    const dataChange = (e) => {
        const newData = {
            ...newUser,
            [e.target.name]: e.target.value,
        };
        if (typeof onChange === 'function') {
            onChange(newData);
        }
        setUser(newData);
    };

    return (
        <ValidationForm onSubmit={onSubmit} ref={ref}>
            {showWarning && (
                <Box bgColor="neutral50" padding="sm" rounded="sm" mb="md">
                    <P mb="0" icon={(<AlertTriangle />)}>
                        This is not a substitution. You are editing {user?.fname} {user?.lname}. Any edits you make to this player will apply to all teams this player is on.
                    </P>
                </Box>
            )}
            <Grid bgColor="white" columns="1fr 1fr 1fr 1fr" gridGap={{ phone: 'sm', laptop: 'lg' }} mb="md">
                {showName && (
                    <>
                        <Cell column={columns}>
                            <Text value={user?.fname} label="First Name" name="fname" rules={{ required: true, minlength: 3 }} onInput={dataChange} />
                        </Cell>
                        <Cell column={columns}>
                            <Text value={user?.lname} label="Last Name" name="lname" rules={{ required: true, minlength: 3 }} onInput={dataChange} />
                        </Cell>
                    </>
                )}
                {showType && (
                    <Cell column="span 4">
                        <Radio
                            value={user.type}
                            name="type"
                            label="Primary Role"
                            columns="repeat(auto-fit, minMax(120px, 1fr))"
                            options={[{
                                label: 'Player',
                                value: 'player',
                            }, {
                                label: 'Parent/Relative',
                                value: 'parent',
                            }, {
                                label: 'Coach',
                                value: 'coach',
                            }]}
                            onChange={(e) => setRole(e.target.value)}
                            size="sm"
                            uncheckedOutline
                            mb="lg"
                            rules={{ required: true }}
                        />
                    </Cell>
                )}
                {role === 'player' && (
                    <>
                        {showGender && (
                            <Cell column={columns}>
                                <Radio
                                    value={user.gender}
                                    name="gender"
                                    columns="1fr 1fr"
                                    color="secondary"
                                    options={[{
                                        label: 'Male',
                                        value: 'm',
                                    }, {
                                        label: 'Female',
                                        value: 'f',
                                    }]}
                                    label="Gender"
                                    size="sm"
                                    uncheckedOutline
                                    onChange={dataChange}
                                    rules={{ required: true }}
                                />
                            </Cell>
                        )}
                        <Cell column={showGender ? columns : 'span 4'}>
                            <DOB
                                name="dob"
                                value={user.dob}
                                onInput={dataChange}
                                rules={{ required: true }}
                            />
                        </Cell>
                        <Cell column="span 4">
                            <FieldHeight
                                font="p"
                                label="Height"
                                value={user.height}
                                name="height" 
                                color="secondary"
                                onInput={dataChange}
                            />
                        </Cell>
                        <Cell column={columns}>
                            <Experience
                                name="experience"
                                value={user.experience ? user.experience.replace('_', '') : ' '}
                                onChange={dataChange}
                                rules={{ required: true }}
                            />
                        </Cell>
                        <Cell column={columns}>
                            <Tshirt
                                name="tshirt"
                                onChange={dataChange}
                                value={user.tshirt ? user.tshirt.replace('_', '') : ''}
                            />
                        </Cell>
                    </>
                )}
            </Grid>
            {children}
        </ValidationForm>
    );
}

export default UserProfile;